import React, { Dispatch, SetStateAction } from 'react';
import PeopleIcon from '@mui/icons-material/People';
import {
    Autocomplete,
    Divider,
    IconButton,
    ListItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import { GroupSharingT, UserSharingT } from 'business/definitions';
import useGetUserGroups from 'hooks/userGroups/useGetUserGroups';
import { SharedItemUI } from './SharedItemUI';
import SelectedOptionsList from './components/SelectedOptionsList';
import {
    addUsers,
    getUserGroupsOptions,
    getUsersOptions,
    isUserSharing,
} from './helpers';

const StyGroupHeader = styled(Typography, { name: 'StyGroupHeader' })(
    ({ theme }) => ({
        padding: theme.spacing(1),
        color: theme.palette.primary.main,
    }),
);
type UsersSharingProps = {
    allUsers: Array<string>;
    selectedUsers: UserSharingT[];
    setSelectedUsers: Dispatch<SetStateAction<UserSharingT[]>>;
    shouldHandleUserGroups: boolean;
    selectedUserGroups: GroupSharingT[];
    setSelectedUserGroups: Dispatch<SetStateAction<GroupSharingT[]>>;
    ownerMail?: string;
    translationKey: string;
    disabled?: boolean;
    className?: string;
};

const UsersSharing = ({
    allUsers,
    selectedUsers,
    setSelectedUsers,
    shouldHandleUserGroups,
    selectedUserGroups,
    setSelectedUserGroups,
    ownerMail = undefined,
    translationKey,
    disabled = false,
    className: classNameProp,
}: UsersSharingProps): React.ReactElement => {
    const { t } = useTranslation();
    const selectedOptionsList = [...selectedUserGroups, ...selectedUsers];

    const { data: userGroups = [] } = useGetUserGroups(shouldHandleUserGroups);

    const AutoComplete = (
        <Autocomplete
            disableClearable
            multiple
            options={[
                ...getUserGroupsOptions(userGroups, selectedUserGroups),
                ...getUsersOptions(allUsers, selectedUsers),
            ]}
            noOptionsText={t('common.noResult')}
            disableCloseOnSelect
            data-testid="autocomplete_addshare"
            getOptionLabel={(option) => {
                if (isUserSharing(option)) return option.email;
                return option.groupName;
            }}
            groupBy={
                shouldHandleUserGroups
                    ? (option) => {
                          if (isUserSharing(option))
                              return t('sharingModal.dashboard.users');
                          return t('sharingModal.dashboard.userGroups');
                      }
                    : undefined
            }
            isOptionEqualToValue={(option, value) => option.key === value.key}
            renderGroup={(params) => (
                <li>
                    <StyGroupHeader variant="body1">
                        {params.group}
                    </StyGroupHeader>
                    <Divider />
                    {params.children}
                </li>
            )}
            renderOption={(props, option) => (
                <ListItem
                    {...props}
                    onClick={() => {
                        if (isUserSharing(option)) {
                            addUsers([option], selectedUsers, setSelectedUsers);
                            return;
                        }
                        setSelectedUserGroups([...selectedUserGroups, option]);
                    }}
                >
                    {isUserSharing(option)
                        ? option.email
                        : `${option.groupName} (${option.nbMembers})`}
                </ListItem>
            )}
            sx={{ minWidth: '18.75rem' }}
            value={selectedOptionsList}
            renderInput={(params) => (
                <TextField {...params} label="" variant="standard" />
            )}
            renderTags={() => <></>}
        />
    );

    const userSharing = (
        <ListItem disabled={disabled}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {t(`sharingModal.${translationKey}.addUser`)}
                        </TableCell>
                        <TableCell>{AutoComplete}</TableCell>
                        <TableCell />
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                            {t(`sharingModal.${translationKey}.haveAccess`)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                            {t('sharingModal.writeAccess')}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ownerMail && (
                        <TableRow key="owner-1">
                            <TableCell>
                                {`${ownerMail} (${t('sharingModal.creator')})`}
                            </TableCell>
                            <TableCell>
                                <Switch disabled checked />
                            </TableCell>
                            <TableCell>
                                <IconButton disabled>
                                    <CancelIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    )}
                    <SelectedOptionsList
                        selectedOptions={selectedOptionsList}
                        disabled={disabled}
                        setSelectedUsers={setSelectedUsers}
                        setSelectedUserGroups={setSelectedUserGroups}
                    />
                </TableBody>
            </Table>
        </ListItem>
    );

    return (
        <SharedItemUI
            className={classNameProp}
            title={t(`sharingModal.userSharingTitle`)}
            subtitle={t(`sharingModal.${translationKey}.userSharingSubtitle`)}
            onCheck={() => null}
            checkboxVisible={false}
            icon={<PeopleIcon />}
        >
            {userSharing}
        </SharedItemUI>
    );
};

const StyUsersSharing = styled(UsersSharing, {
    name: 'StyUsersSharing',
})<UsersSharingProps>({
    '& .MuiTableCell-root': {
        borderBottom: 'none',
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        letterSpacing: '0.42855px',
        color: '#3F4750',
        lineHeight: '22px',
        paddingLeft: '0px',
    },
    '& .MuiTableCell-head': {
        padding: '0.875rem',
        paddingLeft: '0px',
        paddingBottom: '0px',
    },
});

export default StyUsersSharing;
